import { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  IconButton,
  Alert,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  useTheme,
} from "@mui/material";
import {
  Undo as UndoIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  BrokenImage as BrokenImageInfo,

} from "@mui/icons-material";

/**
 * DrawingCanvas - Componente para dibujar regiones sobre imagen/stream de video
 *
 * Modos soportados:
 * - "lines": Múltiples líneas (Counter)
 * - "directional-line": Una línea con dirección (Queue)
 * - "polygon": Polígonos de múltiples puntos (Region Detection)
 *
 * Props:
 * - mode: "lines" | "directional-line" | "polygon"
 * - imageSource: URL de imagen o null (futuro: stream del proyecto GPU)
 * - initialData: Datos iniciales (líneas/polígonos existentes)
 * - onSave: Callback cuando se guarda (recibe coordenadas normalizadas)
 * - onCancel: Callback cuando se cancela
 */
const DrawingCanvas = ({
  mode = "lines",
  imageSource = null,
  initialData = null,
  onSave,
  onCancel,
  title = "Dibujar Región",
}) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [currentPoints, setCurrentPoints] = useState([]);
  const [shapes, setShapes] = useState([]); // Array de líneas o polígonos completados
  const [uploadedImage, setUploadedImage] = useState(null);
  const [direction, setDirection] = useState("down"); // Para Queue
  const [lineName, setLineName] = useState(""); // Para Counter

  // Nuevos estados para Cámara
  const [selectedCamera, setSelectedCamera] = useState("");
  const [useStream, setUseStream] = useState(true);
  const [streamError, setStreamError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Para forzar redraw periódico

  const loadInitialData = useCallback((data) => {
    if (mode === "lines") {
      // data = [{start: [x,y], end: [x,y], name: "Line-1"}]
      setShapes(data || []);
    } else if (mode === "directional-line") {
      // data = {line_start: [x,y], line_end: [x,y], direction: "down"}
      if (data && data.line_start && data.line_end) {
        setShapes([{ start: data.line_start, end: data.line_end }]);
        setDirection(data.direction || "down");
      }
    } else if (mode === "polygon") {
      // data = [[[x1,y1], [x2,y2], ...]] (array de polígonos)
      setShapes(data || []);
    }
  }, [mode]);

  // Cargar datos iniciales
  useEffect(() => {
    if (initialData) {
      loadInitialData(initialData);
    }
  }, [initialData, loadInitialData]);

  // Helpers de dibujo
  const drawPoint = useCallback((ctx, point, color, size) => {
    const x = point[0] * canvasSize.width;
    const y = point[1] * canvasSize.height;

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fill();

    // Borde
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [canvasSize]);

  const drawLine = useCallback((ctx, start, end, color, width, labelIdx = null) => {
    const x1 = start[0] * canvasSize.width;
    const y1 = start[1] * canvasSize.height;
    const x2 = end[0] * canvasSize.width;
    const y2 = end[1] * canvasSize.height;

    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Puntos en los extremos
    drawPoint(ctx, start, color, 5);
    drawPoint(ctx, end, color, 5);

    // Flecha en el medio (dirección)
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const angle = Math.atan2(y2 - y1, x2 - x1);

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(mx - 10 * Math.cos(angle - Math.PI / 6), my - 10 * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(mx - 10 * Math.cos(angle + Math.PI / 6), my - 10 * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();

    // Label
    if (labelIdx !== null) {
      ctx.fillStyle = "#fff";
      ctx.font = "14px Arial";
      ctx.fillText(`Línea ${labelIdx + 1}`, x1 + 10, y1 - 10);
    }
  }, [canvasSize, drawPoint]);

  const drawPolygon = useCallback((ctx, points, color, width, closed) => {
    if (points.length === 0) return;

    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.fillStyle = color + "33"; // Semi-transparente

    ctx.beginPath();
    const x0 = points[0][0] * canvasSize.width;
    const y0 = points[0][1] * canvasSize.height;
    ctx.moveTo(x0, y0);

    for (let i = 1; i < points.length; i++) {
      const x = points[i][0] * canvasSize.width;
      const y = points[i][1] * canvasSize.height;
      ctx.lineTo(x, y);
    }

    if (closed) {
      ctx.closePath();
      ctx.fill();
    }
    ctx.stroke();

    // Dibujar puntos
    points.forEach((p) => drawPoint(ctx, p, color, 5));
  }, [canvasSize, drawPoint]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Dibujar imagen de fondo si existe
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvasSize.width, canvasSize.height);
    } else {
      // Placeholder grid si no hay imagen
      ctx.fillStyle = getComputedStyle(canvas).getPropertyValue("--dashboard-bg").trim() || "#090d16";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Grid pattern
      ctx.strokeStyle = getComputedStyle(canvas).getPropertyValue("--dashboard-border").trim() || "#343e49";
      ctx.lineWidth = 1;
      for (let i = 0; i < canvas.width; i += 50) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for (let i = 0; i < canvas.height; i += 50) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }
    }

    // Dibujar formas completadas
    shapes.forEach((shape, idx) => {
      if (mode === "lines" || mode === "directional-line") {
        drawLine(ctx, shape.start, shape.end, "#47d6ff", 3, idx);
      } else if (mode === "polygon") {
        drawPolygon(ctx, shape, "#47d6ff", 2, true);
      }
    });

    // Dibujar forma actual en progreso
    if (currentPoints.length > 0) {
      if (mode === "lines" || mode === "directional-line") {
        if (currentPoints.length === 1) {
          drawPoint(ctx, currentPoints[0], "#f5bc54", 6);
        } else if (currentPoints.length === 2) {
          drawLine(ctx, currentPoints[0], currentPoints[1], "#f5bc54", 2);
        }
      } else if (mode === "polygon") {
        drawPolygon(ctx, currentPoints, "#f5bc54", 2, false);
      }
    }
  }, [canvasSize, shapes, currentPoints, mode, drawLine, drawPolygon, drawPoint]);

  // Redibujar canvas cuando cambian las formas o el trigger de refresco
  useEffect(() => {
    redrawCanvas();
  }, [shapes, currentPoints, canvasSize, refreshTrigger, redrawCanvas, theme.palette.mode]);

  // Cargar imagen (Subida, Stream o Prop)
  useEffect(() => {
    const handleImageLoad = (url, isStream) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        imageRef.current = img;
        if (isStream) setStreamError(false);

        // Ajustar canvas al tamaño de la imagen manteniendo aspect ratio
        // Solo si cambió la fuente significativamente o es la primera carga
        const maxWidth = 800;
        const maxHeight = 600;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Evitar resize constante si ya está cerca (para previews que pueden variar 1px)
        if (Math.abs(canvasSize.width - width) > 5 || Math.abs(canvasSize.height - height) > 5) {
          setCanvasSize({ width, height });
        }
        redrawCanvas();
      };

      img.onerror = () => {
        console.error("Error cargando imagen/stream:", url);
        if (isStream) {
          setStreamError(true);
          imageRef.current = null; // Quitar referencia corrupta
        }
        redrawCanvas(); // Para mostrar placeholder o error
      };

      img.src = url;
    };

    // Prioridad: 1. Imagen Subida
    if (uploadedImage) {
      handleImageLoad(uploadedImage, false);
    }
    // Prioridad: 2. Stream Cámara (si está seleccionado y activo)
    else if (selectedCamera && useStream) {
      const streamUrl = `/video_feed/${selectedCamera}`;
      handleImageLoad(streamUrl, true);
    }
    // Prioridad: 3. ImageSource (prop original)
    else if (imageSource) {
      handleImageLoad(imageSource, false);
    }
    // Fallback: Nada (Placeholder)
    else {
      imageRef.current = null;
      redrawCanvas();
    }
  }, [imageSource, uploadedImage, selectedCamera, useStream, canvasSize, redrawCanvas]);

  // Intervalo de refresco para Stream (Preview mode)
  useEffect(() => {
    let interval;
    // Solo activar si estamos viendo el stream
    const isViewingStream = selectedCamera && useStream && !uploadedImage && !streamError;

    if (isViewingStream) {
      interval = setInterval(() => {
        // Forzamos un re-render del canvas 
        // (si es MJPEG, imageRef.current ya tiene el frame nuevo, solo hay que pintarlo)
        setRefreshTrigger(prev => prev + 1);
      }, 500); // 500ms
    }

    return () => clearInterval(interval);
  }, [selectedCamera, useStream, uploadedImage, streamError]);

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / canvasSize.width;
    const y = (e.clientY - rect.top) / canvasSize.height;

    // Normalizar a 0-1
    const normalizedPoint = [
      Math.max(0, Math.min(1, x)),
      Math.max(0, Math.min(1, y))
    ];

    if (mode === "lines") {
      // Click para líneas: 2 clics = 1 línea
      if (currentPoints.length === 0) {
        setCurrentPoints([normalizedPoint]);
      } else if (currentPoints.length === 1) {
        const newLine = {
          start: currentPoints[0],
          end: normalizedPoint,
          name: lineName || `Line-${shapes.length + 1}`
        };
        setShapes([...shapes, newLine]);
        setCurrentPoints([]);
        setLineName(""); // Reset
      }
    } else if (mode === "directional-line") {
      // Solo 1 línea permitida
      if (shapes.length > 0) {
        alert("Solo se permite una línea en modo Queue. Borra la existente primero.");
        return;
      }

      if (currentPoints.length === 0) {
        setCurrentPoints([normalizedPoint]);
      } else if (currentPoints.length === 1) {
        const newLine = {
          start: currentPoints[0],
          end: normalizedPoint
        };
        setShapes([newLine]);
        setCurrentPoints([]);
      }
    } else if (mode === "polygon") {
      setCurrentPoints([...currentPoints, normalizedPoint]);
    }
  };

  const handleCanvasDoubleClick = (e) => {
    if (mode === "polygon" && currentPoints.length >= 3) {
      // Cerrar polígono con doble click
      console.log("🔒 Cerrando polígono con doble-click:", currentPoints);
      setShapes([...shapes, currentPoints]);
      setCurrentPoints([]);
    }
  };

  const handleClosePolygon = () => {
    if (mode === "polygon" && currentPoints.length >= 3) {
      console.log("🔒 Cerrando polígono con botón:", currentPoints);
      setShapes([...shapes, currentPoints]);
      setCurrentPoints([]);
    }
  };

  const handleUndo = () => {
    if (currentPoints.length > 0) {
      setCurrentPoints(currentPoints.slice(0, -1));
    } else if (shapes.length > 0) {
      setShapes(shapes.slice(0, -1));
    }
  };

  const handleClear = () => {
    setCurrentPoints([]);
    setShapes([]);
  };

  const handleClearImage = () => {
    setUploadedImage(null);
    // Si hay cámara, volverá al stream automáticamente por el useEffect
    if (selectedCamera) {
      setUseStream(true);
    }
  };

  const handleCameraChange = (e) => {
    setSelectedCamera(e.target.value);
    setUseStream(true); // Activar stream al cambiar
    setStreamError(false);
  };

  const handleReturnToStream = () => {
    setUploadedImage(null);
    setUseStream(true);
  };

  const handleSave = () => {
    let dataToSave;

    if (mode === "lines") {
      dataToSave = shapes; // [{start, end, name}]
    } else if (mode === "directional-line") {
      if (shapes.length === 0) {
        alert("Debes dibujar una línea primero");
        return;
      }
      dataToSave = {
        line_start: shapes[0].start,
        line_end: shapes[0].end,
        direction: direction
      };
    } else if (mode === "polygon") {
      // Si hay puntos sin cerrar, preguntar qué hacer
      if (currentPoints.length > 0) {
        const includeIncomplete = window.confirm(
          `Tienes ${currentPoints.length} punto(s) sin cerrar.\n\n` +
          `¿Deseas cerrar automáticamente este polígono antes de guardar?\n\n` +
          `Click "Aceptar" para cerrar y guardar, o "Cancelar" para guardar solo los polígonos completados.`
        );

        if (includeIncomplete && currentPoints.length >= 3) {
          // Cerrar el polígono actual automáticamente
          dataToSave = [...shapes, currentPoints];
        } else {
          // Guardar solo los completados
          dataToSave = shapes;
        }
      } else {
        dataToSave = shapes; // [[[x,y], [x,y], ...]]
      }
    }

    console.log("🎨 DrawingCanvas - Guardando datos:", {
      mode,
      shapes,
      currentPoints,
      dataToSave
    });

    if (onSave) {
      onSave(dataToSave);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getInstructions = () => {
    if (mode === "lines") {
      return "Click 2 puntos para dibujar una línea. Puedes dibujar múltiples líneas.";
    } else if (mode === "directional-line") {
      return "Click 2 puntos para dibujar la línea de Queue. Selecciona la dirección.";
    } else if (mode === "polygon") {
      return "Click para agregar puntos (mínimo 3). Para cerrar el polígono: Doble-click o usa el botón 'Cerrar Polígono'. Puedes dibujar múltiples polígonos.";
    }
  };

  return (
    <Card sx={{ maxWidth: 900, mx: "auto", mt: 2 }}>
      <CardContent>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={onCancel}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </Stack>
        </Stack>

        {/* Instrucciones */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {getInstructions()}
        </Alert>

        {/* Selector de Cámara y Status */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Seleccionar Cámara</InputLabel>
            <Select
              value={selectedCamera}
              label="Seleccionar Cámara"
              onChange={handleCameraChange}
            >
              <MenuItem value="">
                <em>Ninguna</em>
              </MenuItem>
              <MenuItem value="Camera-1">Camera-1</MenuItem>
              <MenuItem value="Camera-2">Camera-2</MenuItem>
            </Select>
          </FormControl>

          {/* Indicadores de Estado */}
          {uploadedImage ? (
            <Chip
              icon={<ImageIcon />}
              label="Mostrando: Imagen Subida"
              color="primary"
              variant="outlined"
              onDelete={handleClearImage}
            />
          ) : selectedCamera ? (
            streamError ? (
              <Chip
                icon={<BrokenImageInfo />}
                label="Error de Stream"
                color="error"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<VideocamIcon />}
                label={`Stream: ${selectedCamera}`}
                color="success"
                variant="outlined"
              />
            )
          ) : (
            <Chip label="Sin fondo" variant="outlined" />
          )}

          {/* Botón para volver a stream si hay imagen subida y cámara seleccionada */}
          {uploadedImage && selectedCamera && (
            <Button size="small" onClick={handleReturnToStream} startIcon={<VideocamIcon />}>
              Volver a Cámara
            </Button>
          )}
        </Stack>

        {/* Upload de imagen */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImageIcon />}
          >
            {uploadedImage ? "Cambiar Imagen" : "Subir Imagen de Referencia"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
          </Button>

          {(uploadedImage || selectedCamera) && (
            <Button color="warning" onClick={handleClearImage} sx={{ ml: 1 }}>
              Limpiar Fondo
            </Button>
          )}

          {!uploadedImage && !selectedCamera && (
            <Typography variant="caption" sx={{ ml: 2, opacity: 0.7 }}>
              (Opcional) Sube una captura o selecciona una cámara
            </Typography>
          )}
        </Box>

        {/* Alerta de Error de Stream */}
        {streamError && selectedCamera && !uploadedImage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No se pudo conectar con {selectedCamera}. Se muestra el placeholder.
          </Alert>
        )}

        {/* Canvas */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflowX: "auto",
            maxWidth: "100%",
            mb: 2,
            display: "inline-block",
          }}
        >
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onClick={handleCanvasClick}
            onDoubleClick={handleCanvasDoubleClick}
            style={{
              cursor: "crosshair",
              display: "block",
            }}
          />
        </Box>

        {/* Controles */}
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={1}>
            <IconButton
              onClick={handleUndo}
              disabled={currentPoints.length === 0 && shapes.length === 0}
              color="primary"
            >
              <UndoIcon />
            </IconButton>
            <IconButton
              onClick={handleClear}
              disabled={currentPoints.length === 0 && shapes.length === 0}
              color="error"
            >
              <ClearIcon />
            </IconButton>
          </Stack>

          {/* Controles específicos por modo */}
          <Stack direction="row" gap={2} alignItems="center">
            {mode === "directional-line" && (
              <TextField
                select
                label="Dirección"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="up">Arriba</MenuItem>
                <MenuItem value="down">Abajo</MenuItem>
                <MenuItem value="left">Izquierda</MenuItem>
                <MenuItem value="right">Derecha</MenuItem>
              </TextField>
            )}

            {mode === "lines" && currentPoints.length === 1 && (
              <TextField
                label="Nombre de línea"
                value={lineName}
                onChange={(e) => setLineName(e.target.value)}
                size="small"
                placeholder={`Line-${shapes.length + 1}`}
                sx={{ minWidth: 150 }}
              />
            )}

            {mode === "polygon" && currentPoints.length >= 3 && (
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleClosePolygon}
                sx={{ minWidth: 150 }}
              >
                Cerrar Polígono ({currentPoints.length} puntos)
              </Button>
            )}
          </Stack>

          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {mode === "polygon" && (
              <>
                Polígonos completados: {shapes.length} | Puntos en progreso: {currentPoints.length}
                {currentPoints.length > 0 && currentPoints.length < 3 && " (mínimo 3 para cerrar)"}
              </>
            )}
            {(mode === "lines" || mode === "directional-line") && currentPoints.length === 1 && "Click el segundo punto"}
            {mode === "lines" && `Líneas dibujadas: ${shapes.length}`}
          </Typography>
        </Stack>

        {/* Info adicional */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "rgba(255,255,255,0.05)", borderRadius: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            💡 <strong>Tip:</strong> Puedes dibujar sobre el stream en vivo o pausarlo subiendo una captura.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DrawingCanvas;
